import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/ui/collapsible";
import * as api from "../api.js";
import { ICONES, Icone } from "../visual.jsx";

/**
 * A tela da reunião, quando o escritório escolheu guardá-la.
 *
 * Fica FECHADA por padrão, e não por timidez de desenho: o vídeo de uma reunião
 * de uma hora tem centenas de megabytes, e um `<video>` montado no meio do
 * atendimento começa a baixar isso sozinho — no celular de quem está em audiência,
 * inclusive. Abrir é o consentimento de gastar a banda, e é aí que o `src` entra.
 *
 * Por que ele existe: o que decide o caso às vezes não está na voz. O cliente
 * mostra o cartão de ponto na tela, a testemunha aparece na chamada, a outra parte
 * exibe um documento. Isso é prova, e só o vídeo guarda.
 */
export default function VideoDaReuniao({ gravacao }) {
  const [aberto, setAberto] = useState(false);
  const nome = gravacao.video_arquivo || `reuniao-${gravacao.id}-tela.webm`;
  const megas = gravacao.video_bytes ? Math.max(1, Math.round(gravacao.video_bytes / 1_000_000)) : 0;
  const formato = /mp4/i.test(gravacao.video_mime || nome) ? "mp4" : "webm";

  return (
    <Collapsible
      open={aberto}
      onOpenChange={setAberto}
      className="mb-3 overflow-hidden rounded-xl border border-foreground/10 bg-foreground/3"
    >
      <div className="flex items-center gap-2 p-[9px_11px]">
        <Icone de={ICONES.calls} tamanho={15} cor="var(--color-verde-fundo)" />
        <div className="min-w-0 flex-1">
          <div className="text-apoio font-semibold">Tela da reunião</div>
          {/* Formato e peso, não a palavra "vídeo": o rótulo acima já disse o que é,
              e o que decide se vale abrir agora é quanto pesa. */}
          <div className="mt-px font-mono text-selo text-muted-foreground">
            {formato}
            {megas ? ` · ${megas} MB` : ""}
          </div>
        </div>
        <a
          href={api.videoDaGravacao(gravacao.id)}
          download={nome}
          data-dica="Baixar o vídeo"
          aria-label="Baixar o vídeo da reunião"
          className="flex flex-none text-muted-foreground no-underline"
        >
          <Icone de={ICONES.baixar} tamanho={14} />
        </a>
        <CollapsibleTrigger className="flex-none rounded-lg border border-foreground/16 bg-transparent px-[9px] py-[4px] text-selo font-bold">
          {aberto ? "Esconder" : "Ver"}
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        {/* `preload="none"`: mesmo aberto, quem manda o download começar é o play.
            E `aspect-video` para o quadro não pular de altura quando o primeiro
            frame chega — a reunião abre no tamanho em que vai ficar. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a legenda desta reunião é a transcrição, logo abaixo */}
        <video
          src={aberto ? api.videoDaGravacao(gravacao.id) : undefined}
          controls
          preload="none"
          className="aspect-video w-full bg-tinta"
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
